import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormModal from '../common/FormModal';
import { FormInput, FormTextarea, FormButton } from '../common/FormInput';
import { IEpisodeOutline, CreateEpisodeOutlineRequest } from '@packages/global';

interface EpisodeOutlineFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEpisodeOutlineRequest) => Promise<void>;
  projectId: string;
  editingOutline?: IEpisodeOutline | null;
  nextEpisodeNumber?: number;
}

export default function EpisodeOutlineForm({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  editingOutline,
  nextEpisodeNumber = 1
}: EpisodeOutlineFormProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const [formData, setFormData] = useState({
    episodeNumber: nextEpisodeNumber,
    title: '',
    synopsis: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const isEditing = !!editingOutline;

  useEffect(() => {
    if (editingOutline) {
      setFormData({
        episodeNumber: editingOutline.episodeNumber,
        title: editingOutline.title,
        synopsis: editingOutline.synopsis
      });
    } else {
      setFormData({
        episodeNumber: nextEpisodeNumber,
        title: '',
        synopsis: ''
      });
    }
    setErrors({});
  }, [editingOutline, nextEpisodeNumber, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      newErrors.title = t('outline.validation.titleRequired');
    }
    if (!formData.synopsis.trim()) {
      newErrors.synopsis = t('outline.validation.synopsisRequired');
    }
    if (formData.episodeNumber < 1) {
      newErrors.episodeNumber = t('outline.validation.episodeNumberMin');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await onSubmit({
        projectId,
        episodeNumber: formData.episodeNumber,
        title: formData.title.trim(),
        synopsis: formData.synopsis.trim()
      });
      onClose();
    } catch (error) {
      console.error('提交失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('outline.edit') : t('outline.add')}
      size="md"
      footer={
        <>
          <FormButton variant="secondary" onClick={onClose}>
            {t('common:cancel')}
          </FormButton>
          <FormButton variant="primary" loading={loading} onClick={handleSubmit}>
            {isEditing ? t('common:save') : t('common:add')}
          </FormButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="w-24">
            <FormInput
              label={t('outline.episodeNumberLabel')}
              type="number"
              min={1}
              value={formData.episodeNumber}
              onChange={(e) => setFormData({ ...formData, episodeNumber: parseInt(e.target.value) || 1 })}
              error={errors.episodeNumber}
            />
          </div>
          <div className="flex-1">
            <FormInput
              label={t('outline.titleLabel')}
              placeholder={t('outline.titlePlaceholder')}
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              error={errors.title}
            />
          </div>
        </div>

        <FormTextarea
          label={t('outline.synopsisLabel')}
          placeholder={t('outline.synopsisPlaceholder')}
          rows={6}
          value={formData.synopsis}
          onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
          error={errors.synopsis}
        />
      </div>
    </FormModal>
  );
}
